/**
 * Centralized axios client for API requests.
 * In development mode, adds random delay to simulate real network conditions.
 */

import axios from 'axios';
import { config } from '../config';
import { getUserId } from '../analytics';
import { devDelay } from './devDelay';
import { getClientRequestMetadataHeaders } from '../utils/clientTime';
import { getMilestoneResponseGeneration, publishMilestoneResponse } from '../utils/milestoneResponses';

const UPDATE_RELOAD_GUARD_KEY = 'client_update_reload_guard_v2';
const UPDATE_REQUIRED_PENDING_KEY = 'client_update_required_pending_v1';
const UPDATE_RELOAD_IN_PROGRESS_KEY = 'client_update_reload_in_progress_v1';
const UPDATE_RELOAD_MAX_ATTEMPTS_PER_SHA = 1;
const UPDATE_RELOAD_IN_PROGRESS_MAX_AGE_MS = 15 * 1000;
const CLIENT_PORTAL_HEADER = 'X-Client-Portal';
const IS_ADMIN_PORTAL = process.env.REACT_APP_IS_ADMIN_PORTAL === 'true';
const CLIENT_PORTAL_VALUE = IS_ADMIN_PORTAL ? 'admin' : 'web';

export const CLIENT_UPDATE_REQUIRED_EVENT = 'client-update-required';
export const CLIENT_UPDATE_REQUIRED_MESSAGE = 'È disponibile una nuova versione di Giochini di Parole.';
export const CLIENT_UPDATE_REQUIRED_CTA_LABEL = 'Ricarica pagina';

export const shouldUseApiCredentials = (isAdminPortal, nodeEnv = process.env.NODE_ENV) => {
  return isAdminPortal && nodeEnv !== 'development';
};

const normalizeRequiredClientSha = (value) => {
  if (typeof value !== 'string') {
    return 'unknown';
  }
  const normalized = value.trim().toLowerCase();
  return normalized || 'unknown';
};

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const readReloadGuard = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return {};
  }

  try {
    const raw = storage.getItem(UPDATE_RELOAD_GUARD_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return parsed;
  } catch (error) {
    return {};
  }
};

const writeReloadGuard = (guardBySha) => {
  const storage = getSessionStorage();
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(UPDATE_RELOAD_GUARD_KEY, JSON.stringify(guardBySha));
    return true;
  } catch (error) {
    // Ignore sessionStorage write failures.
    return false;
  }
};

const readReloadInProgress = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(UPDATE_RELOAD_IN_PROGRESS_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const requiredClientSha = normalizeRequiredClientSha(parsed.requiredClientSha);
    const startedAt = Number(parsed.startedAt || 0);
    return { requiredClientSha, startedAt };
  } catch (error) {
    return null;
  }
};

const clearReloadInProgress = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(UPDATE_RELOAD_IN_PROGRESS_KEY);
  } catch (error) {
    // Ignore sessionStorage write failures.
  }
};

const markReloadInProgress = (requiredClientSha) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  const payload = {
    requiredClientSha: normalizeRequiredClientSha(requiredClientSha),
    startedAt: Date.now(),
  };

  try {
    storage.setItem(UPDATE_RELOAD_IN_PROGRESS_KEY, JSON.stringify(payload));
  } catch (error) {
    // Ignore sessionStorage write failures.
  }
};

const isReloadInProgressForSha = (requiredClientSha) => {
  const reloadInProgress = readReloadInProgress();
  if (!reloadInProgress) {
    return false;
  }

  const now = Date.now();
  if (now - reloadInProgress.startedAt > UPDATE_RELOAD_IN_PROGRESS_MAX_AGE_MS) {
    clearReloadInProgress();
    return false;
  }

  return reloadInProgress.requiredClientSha === normalizeRequiredClientSha(requiredClientSha);
};

const shouldAutoReloadForUpdate = (requiredClientSha) => {
  const guardBySha = readReloadGuard();
  const normalizedSha = normalizeRequiredClientSha(requiredClientSha);
  const previousAttempts = Number(guardBySha[normalizedSha] || 0);

  if (previousAttempts >= UPDATE_RELOAD_MAX_ATTEMPTS_PER_SHA) {
    return false;
  }

  guardBySha[normalizedSha] = previousAttempts + 1;
  return writeReloadGuard(guardBySha);
};

const dispatchClientUpdateRequired = (detail = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(CLIENT_UPDATE_REQUIRED_EVENT, { detail }));
};

const persistPendingClientUpdateRequired = (detail = {}) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(UPDATE_REQUIRED_PENDING_KEY, JSON.stringify(detail));
  } catch (error) {
    // Ignore sessionStorage write failures.
  }
};

export const consumePendingClientUpdateRequired = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(UPDATE_REQUIRED_PENDING_KEY);
    if (!raw) {
      return null;
    }

    storage.removeItem(UPDATE_REQUIRED_PENDING_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

// If a previous runtime initiated reload, the new runtime starts clean.
clearReloadInProgress();

// Create axios instance
const apiClient = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: shouldUseApiCredentials(IS_ADMIN_PORTAL),
  timeout: 30000,
});

// Request interceptor: Add user ID header
apiClient.interceptors.request.use((reqConfig) => {
  reqConfig.milestoneGeneration = getMilestoneResponseGeneration();
  reqConfig.headers = reqConfig.headers || {};

  const userId = getUserId();
  if (userId) {
    reqConfig.headers['X-User-ID'] = userId;
  }
  if (config.GIT_SHA) {
    reqConfig.headers['X-Client-Git-Sha'] = config.GIT_SHA;
  }
  reqConfig.headers[CLIENT_PORTAL_HEADER] = CLIENT_PORTAL_VALUE;

  const clientContextHeaders = getClientRequestMetadataHeaders?.() || {};
  Object.entries(clientContextHeaders).forEach(([headerName, headerValue]) => {
    reqConfig.headers[headerName] = headerValue;
  });

  return reqConfig;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor: Add dev delay in development mode
apiClient.interceptors.response.use(
  async (response) => {
    await devDelay(response.config.url);
    if (!IS_ADMIN_PORTAL) publishMilestoneResponse(response.data, response.config.milestoneGeneration);
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const errorPayload = error?.response?.data;

    if (!IS_ADMIN_PORTAL && status === 426) {
      if (typeof window !== 'undefined') {
        const requestUrl = error?.config?.url || 'unknown';
        const requiredClientSha = normalizeRequiredClientSha(
          errorPayload?.required_client_sha || errorPayload?.server_git_sha || 'unknown'
        );
        console.info('[ClientUpdate] 426 intercepted for URL:', requestUrl, 'required SHA:', requiredClientSha);

        if (isReloadInProgressForSha(requiredClientSha)) {
          console.info('[ClientUpdate] Reload already in progress for required SHA, suppressing prompt:', requiredClientSha);
        } else if (shouldAutoReloadForUpdate(requiredClientSha)) {
          markReloadInProgress(requiredClientSha);
          console.info('[ClientUpdate] Automatic reload triggered for required SHA:', requiredClientSha);
          window.location.reload();
        } else {
          clearReloadInProgress();
          console.info('[ClientUpdate] Showing blocking update prompt for required SHA:', requiredClientSha);
          const detail = {
            message: CLIENT_UPDATE_REQUIRED_MESSAGE,
            cta_label: CLIENT_UPDATE_REQUIRED_CTA_LABEL,
            required_client_sha: requiredClientSha,
          };
          persistPendingClientUpdateRequired(detail);
          dispatchClientUpdateRequired(detail);
        }
      }
    }

    // Also delay errors in development to simulate real conditions
    await devDelay('error response');
    return Promise.reject(error);
  }
);

export default apiClient;
export { apiClient };
